import { useEffect, useRef, useState } from 'react';
import type { PopulatedMenu } from 'root/apiTypes';
import type { NavigateToFn } from 'root/clientTypes';
import type { DropdownOptionProps } from 'wix-ui-tpa/cssVars';
import { type ILocation, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from 'root/components/Menus/stylesParams';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import { useSharedNavigationLogic } from 'root/components/Menus/NavigationWithSections/Navigation.shared.logic';
import { useSectionRefs } from 'root/contexts/SectionRefsContext';
import { findSectionIndex, getNavigationOffset, getScrollTop, getSectionIdFromSection } from './utils';
import { observeStickyNavigationOffset } from './stickyNavigation';
import { MenuListNavigation, MenuNavigationObject, SECTION_ID_URL, SECTION_ID_URL_PREFIX } from 'root/consts';
import { useNavigationBi } from 'root/components/Menus/Navigation/Navigation.bi';
import { useGetLayoutStylesParams } from 'root/hooks/useGetLayoutStylesParams';
import { getIsStretchFullWidth } from 'root/components/Menus/stylesParamsUtils';
import { useMenusContext } from 'root/contexts/MenusContext';

type UseNavigationWithSectionsLogicParams = {
  menus: PopulatedMenu[];
  setActiveItem: (index: number) => void;
  navigateTo: NavigateToFn;
  currentUrl: ILocation['url'];
  activeMenuIndex: number;
};

export const useNavigationWithSectionsLogic = ({
  menus,
  setActiveItem,
  navigateTo,
  currentUrl = '',
  activeMenuIndex,
}: UseNavigationWithSectionsLogicParams) => {
  const { selectMenu } = useSharedNavigationLogic({
    menus,
    setActiveItem,
    navigateTo,
    currentUrl,
  });
  const { getSectionRef, sectionRefs } = useSectionRefs();
  const [activeSection, setActiveSection] = useState<number | undefined>(undefined);
  const isProgrammaticScroll = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastProgremmaticScrollPosition = useRef<number | undefined>(undefined);
  const navigationContainerRef = useRef<HTMLDivElement>(null);
  const { reportMenuNavigationClick } = useNavigationBi();
  const { showTabs } = useGetDisplayStylesParams();
  const styles = useStyles();
  const {
    preset,
    gridUnifiedNavigationFullWidthTab,
    centerUnifiedNavigationFullWidthTab,
    sideBySideUnifiedNavigationFullWidthTab,
  } = useGetLayoutStylesParams();
  const { isRTL } = useMenusContext();

  const navigationDropdownStyle = styles.get(stylesParams.navigationDropdownStyle);

  const { t } = useTranslation();

  useEffect(() => {
    if (!navigationContainerRef.current) {
      return;
    }
    return observeStickyNavigationOffset(navigationContainerRef.current);
  }, []);
  const isStretchFullWidth = getIsStretchFullWidth({
    preset,
    gridStretchFullWidth: gridUnifiedNavigationFullWidthTab,
    centerStretchFullWidth: centerUnifiedNavigationFullWidthTab,
    sideBySideStretchFullWidth: sideBySideUnifiedNavigationFullWidthTab,
  });

  const shouldShowMenusDropdown = menus.length > 1 && showTabs;

  const onClickNavigationDropdownOption = (option: DropdownOptionProps) => {
    const urlWithoutHash = window.location.href.split('#')[0];
    window.history.replaceState(null, '', urlWithoutHash);
    selectMenu(Number(option.id));
    setActiveSection(0);
    programmaticScrollTo(0, 'auto', 0);
  };

  const dropdownOptions = menus.map((menu, index): DropdownOptionProps => {
    return {
      id: index.toString(),
      value: menu?.name,
      isSelectable: true,
    };
  });

  const programmaticScrollTo = (targetTop: number, behavior: ScrollBehavior = 'smooth', timeout: number = 1000) => {
    isProgrammaticScroll.current = true;
    window.scrollTo({
      top: targetTop,
      behavior,
    });
    setTimeout(() => {
      lastProgremmaticScrollPosition.current = window.scrollY;
      isProgrammaticScroll.current = false;
    }, timeout);
  };

  const handleTabClick = (index: number) => {
    if (index !== undefined) {
      const sectionId = menus[activeMenuIndex].sections[index].id;
      if (sectionId) {
        const targetElementRef = getSectionRef(menus[activeMenuIndex].sections[index].id ?? '');
        const clampedScrollTop = getScrollTop(targetElementRef, getNavigationOffset(navigationContainerRef.current));
        if (clampedScrollTop) {
          setActiveSection(index);
          window.history.replaceState(null, '', SECTION_ID_URL(sectionId));
          programmaticScrollTo(clampedScrollTop);
        }
      }
      reportMenuNavigationClick({
        navigationLayout: MenuListNavigation.Tabs,
        object: MenuNavigationObject.Sections,
        numOfSections: menus[activeMenuIndex].sections.length,
      });
    }
  };

  const createObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!window.IntersectionObserver) {
      return;
    }

    observerRef.current = new window.IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current || window.scrollY === lastProgremmaticScrollPosition.current) {
          return;
        }
        lastProgremmaticScrollPosition.current = undefined;

        const firstIntersectingEntry = entries.find((entry) => entry.isIntersecting);
        if (firstIntersectingEntry) {
          const sectionId = getSectionIdFromSection(firstIntersectingEntry.target.id);
          const sectionIndex = findSectionIndex(menus[activeMenuIndex].sections, sectionId);
          setActiveSection(sectionIndex);
          window.history.replaceState(null, '', SECTION_ID_URL(sectionId));
        }
      },
      { threshold: 0.6 }
    );

    sectionRefs.forEach((ref) => {
      if (ref?.current && observerRef.current) {
        observerRef.current.observe(ref.current);
      }
    });
  };

  useEffect(() => {
    if (!sectionRefs) {
      return;
    }
    const initialSectionTabId = window.location.hash.replace(SECTION_ID_URL_PREFIX, '');
    const initialSectionIndex =
      menus.length > 0 && activeMenuIndex >= 0 && activeMenuIndex < menus.length
        ? menus[activeMenuIndex]?.sections?.findIndex((section) => section.id === initialSectionTabId)
        : -1;
    const initialSectionTabIndex = initialSectionIndex !== -1 ? initialSectionIndex : undefined;
    if (initialSectionTabIndex === undefined) {
      setActiveSection(undefined);
    } else if (initialSectionTabIndex !== activeSection) {
      const targetElementRef = getSectionRef(initialSectionTabId ?? '');

      const clampedScrollTop = getScrollTop(targetElementRef, getNavigationOffset(navigationContainerRef.current));
      if (clampedScrollTop) {
        setActiveSection(initialSectionTabIndex);
        programmaticScrollTo(clampedScrollTop);
      }
    }
    createObserver();

    // Cleanup observer on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sectionRefs, currentUrl]);

  return {
    isRTL,
    dropdownOptions,
    onClickNavigationDropdownOption,
    navigationDropdownStyle,
    t,
    shouldShowMenusDropdown,
    activeSection,
    showSectionNavigation: menus[activeMenuIndex]?.sections?.length > 0,
    navigationContainerRef,
    handleTabClick,
    isStretchFullWidth,
  };
};
